<script setup>
import { ref, computed, reactive } from 'vue';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength, email } from '@vuelidate/validators';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import VueHcaptcha from '@hcaptcha/vue3-hcaptcha';
import { register } from '../../../../../api/auth';

const MIN_PASSWORD_LENGTH = 8;

const props = defineProps({
  // Optional pre-fill, e.g. when the user came from verify-email's
  // "Change email address" to correct a mistyped address.
  initialEmail: { type: String, default: '' },
});

const store = useStore();
const router = useRouter();

const hCaptcha = ref(null);
const isSignupInProgress = ref(false);
const emailServerError = ref('');
const showPassword = ref(false);
const showPasswordConfirm = ref(false);

const credentials = reactive({
  accountName: '',
  fullName: '',
  email: props.initialEmail || '',
  password: '',
  passwordConfirm: '',
  acceptTerms: false,
  hCaptchaClientResponse: '',
});

const meetsAllPasswordRules = value =>
  typeof value === 'string' &&
  value.length >= MIN_PASSWORD_LENGTH &&
  /[0-9]/.test(value) &&
  /[^a-zA-Z0-9]/.test(value);

const matchesPassword = value =>
  typeof value === 'string' &&
  value.length > 0 &&
  value === credentials.password;

const rules = {
  credentials: {
    accountName: { required, minLength: minLength(2) },
    fullName: { required },
    email: { required, email },
    password: { required, meetsAllPasswordRules },
    passwordConfirm: { required, matchesPassword },
  },
};

const v$ = useVuelidate(rules, { credentials });

const globalConfig = computed(() => store.getters['globalConfig/get']);

const isFormValid = computed(
  () => !v$.value.$invalid && credentials.acceptTerms
);

const passwordChecks = computed(() => {
  const v = credentials.password || '';
  const c = credentials.passwordConfirm || '';
  return [
    { key: 'length', label: `At least ${MIN_PASSWORD_LENGTH} characters`, ok: v.length >= MIN_PASSWORD_LENGTH },
    { key: 'number', label: 'At least 1 number', ok: /[0-9]/.test(v) },
    { key: 'symbol', label: 'At least 1 symbol (e.g. ! ? @ # …)', ok: /[^a-zA-Z0-9]/.test(v) },
    { key: 'match', label: 'Both passwords match', ok: v.length > 0 && c.length > 0 && v === c },
  ];
});

const passwordStrength = computed(() => {
  const v = credentials.password || '';
  if (!v.length) return { level: 'none', score: 0 };
  const passed = passwordChecks.value.filter(c => c.ok).length;
  let score = passed;
  if (v.length >= 12) score++;
  if (v.length >= 16) score++;
  if (score <= 1) return { level: 'weak', score };
  if (score <= 3) return { level: 'medium', score };
  return { level: 'strong', score };
});

const performRegistration = async () => {
  isSignupInProgress.value = true;
  emailServerError.value = '';
  try {
    await register(credentials);
    router.push({
      name: 'auth_verify_email',
      state: { email: credentials.email },
    });
  } catch (error) {
    let errorMessage = error?.message || 'Could not create account.';
    // Email already registered → show a clear, actionable message + inline hint.
    if (/e-?mail/i.test(errorMessage) && /(taken|already|exists|registered|in use)/i.test(errorMessage)) {
      errorMessage = 'This email is already registered. Please use a different email address.';
      emailServerError.value = errorMessage;
    }
    if (globalConfig.value.hCaptchaSiteKey) {
      hCaptcha.value?.reset();
      credentials.hCaptchaClientResponse = '';
    }
    useAlert(errorMessage);
  } finally {
    isSignupInProgress.value = false;
  }
};

const submit = () => {
  if (isSignupInProgress.value) return;
  v$.value.$touch();
  if (v$.value.$invalid || !credentials.acceptTerms) return;
  isSignupInProgress.value = true;
  if (globalConfig.value.hCaptchaSiteKey) {
    hCaptcha.value.execute();
  } else {
    performRegistration();
  }
};

const onRecaptchaVerified = token => {
  credentials.hCaptchaClientResponse = token;
  performRegistration();
};

const onCaptchaError = () => {
  isSignupInProgress.value = false;
  credentials.hCaptchaClientResponse = '';
  hCaptcha.value?.reset();
};
</script>

<template>
  <form class="cc-form" @submit.prevent="submit">
    <div class="cc-field">
      <label class="cc-label">Agency name <span class="cc-req">*</span></label>
      <input
        v-model="credentials.accountName"
        class="cc-input"
        :class="{ 'cc-input--error': v$.credentials.accountName.$error }"
        placeholder="e.g. Berlin Talent GmbH"
        autocomplete="organization"
        @blur="v$.credentials.accountName.$touch"
      />
      <div v-if="v$.credentials.accountName.$error" class="cc-hint cc-hint--error">
        Agency name is required (at least 2 characters).
      </div>
    </div>

    <div class="cc-divider" />

    <div class="cc-field">
      <label class="cc-label">Your name <span class="cc-req">*</span></label>
      <input
        v-model="credentials.fullName"
        class="cc-input"
        :class="{ 'cc-input--error': v$.credentials.fullName.$error }"
        placeholder="e.g. Mira Eckhardt"
        autocomplete="name"
        @blur="v$.credentials.fullName.$touch"
      />
      <div v-if="v$.credentials.fullName.$error" class="cc-hint cc-hint--error">
        Your name is required.
      </div>
    </div>

    <div class="cc-field">
      <label class="cc-label">Work email <span class="cc-req">*</span></label>
      <input
        v-model="credentials.email"
        type="email"
        class="cc-input cc-input--mono"
        :class="{ 'cc-input--error': v$.credentials.email.$error }"
        placeholder="you@agency.com"
        autocomplete="email"
        @blur="v$.credentials.email.$touch"
        @input="emailServerError = ''"
      />
      <div v-if="emailServerError" class="cc-hint cc-hint--error">
        {{ emailServerError }}
      </div>
      <div v-else-if="v$.credentials.email.$error" class="cc-hint cc-hint--error">
        Please enter a valid email address.
      </div>
      <div v-else class="cc-hint">
        We'll send a verification link to confirm this address.
      </div>
    </div>

    <div class="cc-field">
      <label class="cc-label">Password <span class="cc-req">*</span></label>
      <div class="cc-pwd-wrap">
        <input
          v-model="credentials.password"
          :type="showPassword ? 'text' : 'password'"
          class="cc-input cc-input--pwd"
          :class="{ 'cc-input--error': v$.credentials.password.$error }"
          placeholder="Choose a strong password"
          autocomplete="new-password"
          @blur="v$.credentials.password.$touch"
        />
        <button
          type="button"
          class="cc-pwd-toggle"
          @click="showPassword = !showPassword"
        >
          {{ showPassword ? 'Hide' : 'Show' }}
        </button>
      </div>
      <div
        v-if="credentials.password.length > 0"
        class="cc-pwd-strength"
        :class="`cc-pwd-strength--${passwordStrength.level}`"
      >
        <div /><div /><div />
      </div>

      <div class="cc-pwd-wrap cc-pwd-wrap--confirm">
        <input
          v-model="credentials.passwordConfirm"
          :type="showPasswordConfirm ? 'text' : 'password'"
          class="cc-input cc-input--pwd"
          :class="{
            'cc-input--error': v$.credentials.passwordConfirm.$error,
            'cc-input--match': credentials.passwordConfirm.length > 0 && credentials.passwordConfirm === credentials.password,
          }"
          placeholder="Confirm your password"
          autocomplete="new-password"
          @blur="v$.credentials.passwordConfirm.$touch"
        />
        <button
          type="button"
          class="cc-pwd-toggle"
          @click="showPasswordConfirm = !showPasswordConfirm"
        >
          {{ showPasswordConfirm ? 'Hide' : 'Show' }}
        </button>
      </div>

      <ul class="cc-pwd-rules">
        <li
          v-for="rule in passwordChecks"
          :key="rule.key"
          :class="{ 'cc-pwd-rule--ok': rule.ok }"
        >
          <svg
            v-if="rule.ok"
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="3"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
          <svg
            v-else
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
          >
            <circle cx="12" cy="12" r="9" />
          </svg>
          <span>{{ rule.label }}</span>
        </li>
      </ul>
    </div>

    <label class="cc-terms">
      <span
        class="cc-checkbox"
        :class="{ 'cc-checkbox--checked': credentials.acceptTerms }"
        @click.prevent="credentials.acceptTerms = !credentials.acceptTerms"
      >
        <svg v-if="credentials.acceptTerms" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round">
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </span>
      <span class="cc-terms-text">
        I agree to the <a :href="globalConfig.termsURL || '#'" target="_blank" rel="noopener">Terms of Service</a> and <a :href="globalConfig.privacyURL || '#'" target="_blank" rel="noopener">Privacy Policy</a>.
      </span>
    </label>

    <VueHcaptcha
      v-if="globalConfig.hCaptchaSiteKey"
      ref="hCaptcha"
      size="invisible"
      :sitekey="globalConfig.hCaptchaSiteKey"
      @verify="onRecaptchaVerified"
      @error="onCaptchaError"
      @expired="onCaptchaError"
      @challenge-expired="onCaptchaError"
      @closed="onCaptchaError"
    />

    <button
      type="submit"
      class="cc-btn-primary"
      :disabled="isSignupInProgress || !isFormValid"
    >
      <template v-if="isSignupInProgress">
        Registering account…
      </template>
      <template v-else>
        Register an account
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round">
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </template>
    </button>

    <div class="cc-eu-line">
      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round">
        <rect x="3" y="11" width="18" height="11" rx="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
      </svg>
      Hosted in EU · GDPR compliant · Your data stays in Europe
    </div>
  </form>
</template>

<style scoped>
.cc-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.cc-field { display: flex; flex-direction: column; }
.cc-label {
  font-family: 'Cinzel', serif;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: #6e7570;
  margin-bottom: 6px;
}
.cc-req { color: #dc2626; font-weight: 800; }
.cc-input {
  width: 100%;
  box-sizing: border-box;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #0b1414;
  padding: 11px 14px;
  border-radius: 9px;
  border: 1px solid #e0d8c5;
  background: #f1ede4;
  outline: none;
  transition: all 0.12s;
}
.cc-input:focus {
  border-color: #0d9488;
  box-shadow: 0 0 0 3px rgba(13, 148, 136, 0.38);
  background: #ffffff;
}
.cc-input--error {
  border-color: #dc2626 !important;
  background: #fef2f2 !important;
}
.cc-input--mono {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13.5px;
}
.cc-input--pwd { padding-right: 80px; }
.cc-input--match {
  border-color: #0d9488;
  background: rgba(13, 148, 136, 0.06);
}
.cc-divider {
  height: 1px;
  background: #e0d8c5;
  margin: 6px 0;
}
.cc-pwd-wrap { position: relative; }
.cc-pwd-wrap--confirm { margin-top: 8px; }
.cc-pwd-toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6e7570;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}
.cc-pwd-strength {
  height: 4px;
  border-radius: 2px;
  background: #e0d8c5;
  overflow: hidden;
  margin-top: 6px;
  display: flex;
  gap: 3px;
}
.cc-pwd-strength > div {
  flex: 1;
  background: #e0d8c5;
  border-radius: 2px;
  transition: background 0.2s;
}
.cc-pwd-strength--weak > div:nth-child(1) { background: #dc2626; }
.cc-pwd-strength--medium > div:nth-child(1),
.cc-pwd-strength--medium > div:nth-child(2) { background: #d97706; }
.cc-pwd-strength--strong > div { background: #0d9488; }

.cc-pwd-rules {
  list-style: none;
  padding: 0;
  margin: 8px 0 0 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.cc-pwd-rules li {
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  line-height: 1.4;
  color: #6e7570;
  transition: color 0.15s;
}
.cc-pwd-rules li svg {
  flex-shrink: 0;
  color: #cfc5ac;
  transition: color 0.15s;
}
.cc-pwd-rules li.cc-pwd-rule--ok {
  color: #0f766e;
  font-weight: 600;
}
.cc-pwd-rules li.cc-pwd-rule--ok svg {
  color: #0d9488;
}
.cc-hint {
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: #6e7570;
  margin-top: 5px;
  line-height: 1.4;
}
.cc-hint--error { color: #dc2626; font-weight: 500; }
.cc-hint--medium { color: #d97706; }
.cc-hint--success { color: #0f766e; font-weight: 600; }

.cc-terms {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  cursor: pointer;
  margin-top: 6px;
}
.cc-checkbox {
  width: 18px;
  height: 18px;
  border-radius: 5px;
  border: 1.5px solid #cfc5ac;
  background: #ffffff;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.12s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.cc-checkbox--checked {
  background: #0d9488;
  border-color: #0d9488;
}
.cc-terms-text {
  font-family: 'Inter', sans-serif;
  font-size: 12.5px;
  color: #2a3434;
  line-height: 1.45;
}
.cc-terms-text a {
  color: #0f766e;
  font-weight: 600;
  text-decoration: none;
}
.cc-terms-text a:hover { text-decoration: underline; }

.cc-btn-primary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 14px;
  border-radius: 10px;
  border: none;
  background: rgba(31, 42, 42, 0.92);
  color: #f5efe0;
  font-family: 'Inter', sans-serif;
  font-size: 14.5px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.12s;
  margin-top: 12px;
  width: 100%;
}
.cc-btn-primary:hover:not(:disabled) {
  background: rgba(11, 20, 20, 0.98);
  transform: translateY(-1px);
  box-shadow: 0 4px 14px rgba(11, 20, 20, 0.2);
}
.cc-btn-primary:disabled {
  background: #cfc5ac;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.cc-eu-line {
  text-align: center;
  font-family: 'Inter', sans-serif;
  font-size: 11.5px;
  color: #6e7570;
  line-height: 1.5;
  margin-top: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
}
</style>
